package com.partejar.app.core.security.presentation

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.sizeIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.partejar.app.core.security.AppLifecycleObserver
import com.partejar.app.core.security.AppLockCoordinator
import com.partejar.app.core.security.AppLockState
import com.partejar.app.core.security.BiometricAuthenticationResult
import com.partejar.app.core.security.BiometricAuthenticator
import com.partejar.app.core.security.BiometricAvailability
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.theme.PartejarIcons
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import partejar_app.shared.generated.resources.Res
import partejar_app.shared.generated.resources.partejar_lockup_dark_transparent
import partejar_app.shared.generated.resources.partejar_lockup_light_transparent

@Composable
fun AppLockHost(
    coordinator: AppLockCoordinator,
    authenticator: BiometricAuthenticator,
    isDarkTheme: Boolean,
    content: @Composable () -> Unit
) {
    val state by coordinator.state.collectAsState()
    val offerRequested by coordinator.activationOfferRequested.collectAsState()
    var attemptedCurrentLock by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) { coordinator.initialize() }
    AppLifecycleObserver(coordinator::onBackground, coordinator::onForeground)

    LaunchedEffect(state) {
        if (state is AppLockState.Locked && !attemptedCurrentLock) {
            attemptedCurrentLock = true
            coordinator.unlock(authenticator)
        } else if (state !is AppLockState.Locked) {
            attemptedCurrentLock = false
        }
    }

    when (state) {
        AppLockState.Unlocked -> {
            when (state) {
                AppLockState.Unlocked -> {
                    if (offerRequested && coordinator.shouldOfferActivation(authenticator)) {
                        // Tela cheia assume o controle: a Home não é renderizada enquanto isso não for resolvido
                        BiometricActivationScreen(
                            onActivate = { coordinator.activate(authenticator) },
                            onDismiss = coordinator::dismissActivationOffer
                        )
                    } else {
                        content() // Só renderiza o app se a biometria já estiver resolvida
                        if (offerRequested) {
                            LaunchedEffect(offerRequested) { coordinator.dismissActivationOffer() }
                        }
                    }
                }
                AppLockState.Checking -> Box(modifier = Modifier.fillMaxSize())
                AppLockState.Locked -> AppLockedScreen(
                    isDarkTheme = isDarkTheme,
                    unavailable = authenticator.availability() != BiometricAvailability.AVAILABLE,
                    onUnlock = { coordinator.unlock(authenticator) },
                    onReturnToLogin = coordinator::returnToLogin
                )
            }
        }
        AppLockState.Checking -> Box(modifier = Modifier.fillMaxSize())
        AppLockState.Locked -> AppLockedScreen(
            isDarkTheme = isDarkTheme,
            unavailable = authenticator.availability() != BiometricAvailability.AVAILABLE,
            onUnlock = { coordinator.unlock(authenticator) },
            onReturnToLogin = coordinator::returnToLogin
        )
    }
}

@Composable
private fun AppLockedScreen(
    isDarkTheme: Boolean,
    unavailable: Boolean,
    onUnlock: suspend () -> BiometricAuthenticationResult,
    onReturnToLogin: () -> Unit
) {
    val scope = rememberCoroutineScope()

    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 32.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Image(
                painter = painterResource(
                    if (isDarkTheme) Res.drawable.partejar_lockup_dark_transparent else Res.drawable.partejar_lockup_light_transparent
                ),
                contentDescription = null,
                contentScale = ContentScale.Fit,
                modifier = Modifier.sizeIn(maxWidth = 220.dp, maxHeight = 165.dp)
            )

            Spacer(modifier = Modifier.height(32.dp))

            Text(
                text = "Acesso restrito",
                style = MaterialTheme.typography.titleLarge,
                color = MaterialTheme.colorScheme.onSurface
            )

            Spacer(modifier = Modifier.height(12.dp))

            Text(
                text = if (unavailable) "A segurança do dispositivo não está disponível agora." else "Confirme sua identidade para continuar.",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                textAlign = TextAlign.Center
            )

            Spacer(modifier = Modifier.height(40.dp))

            if (!unavailable) {
                PartejarButton(
                    text = "Desbloquear",
                    onClick = { scope.launch { onUnlock() } }
                )
                Spacer(modifier = Modifier.height(16.dp))
            }

            Text(
                text = "Entrar com e-mail e senha",
                style = MaterialTheme.typography.labelLarge,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = onReturnToLogin
                    )
                    .padding(12.dp)
            )
        }
    }
}

@Composable
private fun BiometricActivationScreen(
    onActivate: suspend () -> BiometricAuthenticationResult,
    onDismiss: () -> Unit
) {
    val scope = rememberCoroutineScope()

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background)
            .padding(horizontal = 32.dp, vertical = 48.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Spacer(modifier = Modifier.height(32.dp))

        Text(
            text = "Acesso Simplificado",
            style = MaterialTheme.typography.headlineMedium,
            color = MaterialTheme.colorScheme.onBackground,
            textAlign = TextAlign.Center
        )

        Spacer(modifier = Modifier.height(16.dp))

        Text(
            text = "Habilite a biometria para desbloquear o Partejar de forma instantânea e segura nas próximas sessões.",
            style = MaterialTheme.typography.bodyLarge,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            textAlign = TextAlign.Center
        )

        Spacer(modifier = Modifier.weight(1f))

        // Contêiner circular refinado para o ícone
        Box(
            modifier = Modifier
                .size(275.dp)
                .background(
                    color = MaterialTheme.colorScheme.surfaceContainer,
                    shape = CircleShape
                ),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                imageVector = PartejarIcons.Actions.biometric,
                contentDescription = "Ícone de Biometria",
                modifier = Modifier.size(200.dp),
                tint = MaterialTheme.colorScheme.primary
            )
        }

        Spacer(modifier = Modifier.weight(1f))

        PartejarButton(
            text = "Habilitar Biometria",
            onClick = { scope.launch { onActivate() } }
        )

        Spacer(modifier = Modifier.height(16.dp))

        // Ação secundária injetando a cor avermelhada (LightAccent/DarkAccent)
        Text(
            text = "Deixar para depois",
            style = MaterialTheme.typography.labelLarge,
            color = MaterialTheme.colorScheme.tertiary,
            modifier = Modifier
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onDismiss
                )
                .padding(16.dp)
        )

        Spacer(modifier = Modifier.height(24.dp))
    }
}
